import i18n from "@/lib/i18n";

const DEFAULT_CURRENCY = "PLN";
const CURRENCY_SYMBOL = "zł";
const TYPOGRAPHIC_MINUS = "−";

/**
 * Returns CSS class based on money value
 * @param {number|string|{amount: number}|null} value
 * @returns {string} CSS class name
 */
export function moneyClass(value) {
  const variant = moneyVariant(value);
  return variant === "secondary" ? "" : `text-${variant}`;
}

/**
 * Returns the Bootstrap variant of a signed amount: positive green, negative red, zero neutral
 * @param {number|string|{amount: number}|null} value
 * @returns {string} Bootstrap variant
 */
export function moneyVariant(value) {
  const amount = typeof value === "object" && value !== null ? value.amount : value;
  const cents = Math.round(Number(amount) * 100);
  if (cents > 0) return "success";
  if (cents < 0) return "danger";
  return "secondary";
}

/**
 * Returns the legacy tile classes of a signed amount
 * @param {number} value
 * @returns {string} CSS classes
 */
export function moneyTileClass(value) {
  return `text-white bg-${moneyVariant(value)}`;
}

/**
 * Formats currency code
 * @param {string} value - Currency code
 * @returns {string} Translated currency name
 */
export function formatCurrency(value) {
  if (typeof value !== "string" || value.length !== 3) {
    return i18n.t(DEFAULT_CURRENCY);
  }
  return i18n.t(value);
}

/**
 * Formats money value to localized string
 * @param {Object|number|string} value - Money value or object with amount property
 * @param {{ typographicMinus?: boolean }} [options] - typographicMinus rounds to cents and uses "−" instead of "-"
 * @returns {string} Formatted money string
 */
export function formatMoney(value, { typographicMinus = false } = {}) {
  let amount = 0;

  if (value?.amount !== undefined) {
    // Handle object with amount property
    amount = parseFloat(value.amount);
  } else if (typeof value === "number" || typeof value === "string") {
    // Handle direct number or string value
    amount = parseFloat(value);
  }

  // Handle NaN and invalid values
  if (isNaN(amount)) {
    amount = 0;
  }

  if (typographicMinus) {
    const roundedAmount = Math.round(amount * 100) / 100 || 0;

    return i18n
      .n(roundedAmount, "currency")
      .replace(DEFAULT_CURRENCY, CURRENCY_SYMBOL)
      .replace("-", TYPOGRAPHIC_MINUS);
  }

  return i18n.n(amount, "currency").replace(DEFAULT_CURRENCY, CURRENCY_SYMBOL);
}

export function formatUnitPrice(value) {
  let amount = 0;

  if (value?.amount !== undefined) {
    amount = parseFloat(value.amount);
  } else if (typeof value === "number" || typeof value === "string") {
    amount = parseFloat(value);
  }

  if (isNaN(amount)) {
    amount = 0;
  }

  return (
    new Intl.NumberFormat(i18n.locale?.value ?? "pl-PL", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(amount) +
    " " +
    CURRENCY_SYMBOL
  );
}

/**
 * Creates a money object with given amount
 * @param {number} amount
 * @param {string} currency
 * @returns {Object} Money object
 */
export function createMoneyObject(amount = 0, currency = DEFAULT_CURRENCY) {
  return {
    amount: parseFloat(amount) || 0,
    currency,
  };
}
